import { ConfigurationService, IConfigData } from 'services/ConfigurationService';
import { configure, makeAutoObservable } from 'mobx';

import { AppSwitcherIcon } from '@trackman/web-shared-components/features/TopNavigation/IconMenus/AppSwitcher/icons/MenuIcon';
import { ApplicationUserRights } from './ApplicationUserRightsStore';
import { AuthenticationService } from 'services/AuthenticationService';
import LanguageStore from './LanguageStore';
import { NotificationsStore } from './NotificationsStore';
import { ProfileStore } from './ProfileStore';
import StartupService from 'services/StartupService';
import { jwtDecode } from 'jwt-decode';

configure({ enforceActions: 'never' });

type BearerTokenProfileInfo = {
  permissions: string[];
  name: string;
  surname: string;
  userId: string;
  identityUrl: string;
  preferredLanguage: string;
  preferredUnitSystem: string;
  sport: string;
  organizationId: string;
  playerId: string;
  umpireId: string;
  organizationName: string;
};

// Bearer token structure depends on a backend configuration, so all keys are optional
type BearerToken = {
  iss?: string;
  nbf?: number;
  iat?: number;
  exp?: number;
  aud?: string[];
  scope?: string[];
  amr?: string[];
  client_id?: string;
  sub?: string;
  auth_time?: number;
  idp?: string;
  permission?: string[] | string;
  'preferred-language'?: string;
  'preferred-unit-system'?: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'?: string;
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'?: string;
  sport?: string;
  organizationid?: string;
  playerid?: string;
  umpireid?: string;
  organizationname?: string;
  sid?: string;
  jti?: string;
};

export type App = {
  id: string;
  display_name: string;
  description: string;
  group: string;
  icon_name: AppSwitcherIcon;
  client_id: string;
  required_permission: string;
  base_url: string;
  display_name_lng_key: string;
  description_lng_key: string;
  group_lng_key: string;
  updated: string;
};

export class StartupStore {
  private authenticationService: AuthenticationService;
  private configurationService: ConfigurationService;
  private profileStore: ProfileStore;
  private languageStore: LanguageStore;
  private startupService: StartupService;
  private ns: NotificationsStore;

  constructor(
    authenticationService: AuthenticationService,
    configurationService: ConfigurationService,
    profileStore: ProfileStore,
    languageStore: LanguageStore,
    startupService: StartupService,
    ns: NotificationsStore
  ) {
    makeAutoObservable(this);
    this.authenticationService = authenticationService;
    this.configurationService = configurationService;
    (this.profileStore = profileStore), (this.languageStore = languageStore), (this.startupService = startupService);
    this.ns = ns;
  }

  isInitializing: boolean = true; // instantiated with true, so that the content doesn't flash
  hasBearerToken: boolean = false;
  isLoadingConfig: boolean = false;
  isLoadingApps: boolean = false;

  bearerTokenProfileInfo: BearerTokenProfileInfo | null = null;
  config: IConfigData | null = null;
  apps: App[] = [];

  initialize = async (apiServiceAxiosInstance) => {
    this.isInitializing = true;

    await this.getToken();

    await Promise.all([
      this.getConfig(), // needed by Profile menu
      this.getApps(), // needed by AppSwitcher
      this.profileStore.ensureDataAvailability(apiServiceAxiosInstance),
    ]);

    await this.languageStore.initialize();

    this.isInitializing = false;
  };

  getToken = async () => {
    await this.authenticationService.initialize();
    const authData = this.authenticationService.getAuthData();
    if (authData) {
      const decodedToken = jwtDecode<BearerToken>(authData.accessToken);
      const permissionsArray = decodedToken.permission
        ? Array.isArray(decodedToken.permission)
          ? decodedToken.permission
          : [decodedToken.permission]
        : [];
      this.bearerTokenProfileInfo = {
        permissions: permissionsArray,
        name: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'] ?? '',
        surname: decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'] ?? '',
        userId: decodedToken.sub ?? '',
        identityUrl: decodedToken.iss ?? '',
        preferredLanguage: decodedToken['preferred-language'] ?? '',
        preferredUnitSystem: decodedToken['preferred-unit-system'] ?? '',
        sport: decodedToken.sport ?? '',
        organizationId: decodedToken.organizationid ?? '',
        playerId: decodedToken.playerid ?? '',
        umpireId: decodedToken.umpireid ?? '',
        organizationName: decodedToken.organizationname ?? '',
      };
      ApplicationUserRights.setData({ permissions: this.bearerTokenProfileInfo.permissions });
      this.hasBearerToken = true;
    }
  };

  getConfig = async () => {
    try {
      this.isLoadingConfig = true;
      const response = await this.configurationService.getConfigs();
      this.config = response;
    } catch (ex: any) {
      this.ns.displayNotification({
        variant: 'error',
        title: 'Error',
        message: ex.message,
      });
    }
    this.isLoadingConfig = false;
  };

  getApps = async () => {
    if (!this.bearerTokenProfileInfo) return;
    try {
      this.isLoadingApps = true;
      const response = await this.startupService.getAllApps(this.bearerTokenProfileInfo.identityUrl);
      this.apps = response;

      // Using release auth and so a release token, the apps returned will have release urls
      // We need to make sure that if in dev environments, we can navigate between dev apps
      if (this.isDevelopmentPortal()){
        this.apps.forEach(app => {
          // Making sure we only override Football and American Football apps
          if(this.appCanBeOverrided(app)) {
            app.base_url = app.base_url.replace("release-", "dev-");
            app.display_name += " (Dev)";
          }
        })
      }
    } catch (ex: any) {
      this.ns.displayNotification({
        variant: 'error',
        title: 'Error',
        message: ex.message,
      });
    }
    this.isLoadingApps = false;
  };

  isDevelopmentPortal(): boolean{
    return window.location.href.includes("dev-teamportal") || window.location.href.includes("localhost");
  }

  appCanBeOverrided(app: App){
    return app.base_url.includes("portal.trackmansoccer") || app.base_url.includes("portal.trackmanfootballus");
  }
}

export default StartupStore;
